import { SdkContext } from '../internal'
import type {
  GoalCycle,
  GoalCycleInput,
  GoalCycleStatus,
  GoalHealth,
  GoalTree,
  KeyResult,
  KeyResultCheckIn,
  KeyResultInput,
  Objective,
  ObjectiveInput
} from '@/types/goals'

const asList = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items || r?.data || []
}

/**
 * F-50 Metas & OKRs. The community comes from X-CommunityId (never the path);
 * the space from `spaceId`. Who manages a cycle or an objective is the
 * server's (`canManage`); anyone else gets 403/404.
 */
export class GoalsResource {
  constructor(private ctx: SdkContext) {}

  async listCycles(params: { spaceId?: string; status?: GoalCycleStatus } = {}): Promise<GoalCycle[]> {
    return asList<GoalCycle>(await this.ctx.call({ httpMethod: 'GET', path: '/goals/cycles' }, { query: params }))
  }

  /** The cycles and `permissions.canCreateCycle` (owner/admin), which lives outside `data`. */
  async listCyclesWithPermissions(params: { spaceId?: string; status?: GoalCycleStatus } = {}): Promise<{ cycles: GoalCycle[]; canCreateCycle: boolean }> {
    const body = await this.ctx.envelope<{ data?: unknown; permissions?: { canCreateCycle?: boolean } }>({ httpMethod: 'GET', path: '/goals/cycles' }, { query: params })
    return { cycles: asList<GoalCycle>(body?.data), canCreateCycle: Boolean(body?.permissions?.canCreateCycle) }
  }

  async createCycle(body: GoalCycleInput & { title: string; startsOn: string; endsOn: string }): Promise<GoalCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/goals/cycles', hasBody: true }, { body })
  }

  async getCycle(cycleId: string): Promise<GoalCycle> {
    return this.ctx.call({ httpMethod: 'GET', path: '/goals/cycles/{cycleId}' }, { cycleId })
  }

  async updateCycle(cycleId: string, body: GoalCycleInput): Promise<GoalCycle> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/goals/cycles/{cycleId}', hasBody: true }, { cycleId, body })
  }

  async closeCycle(cycleId: string): Promise<GoalCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/goals/cycles/{cycleId}/close' }, { cycleId })
  }

  async tree(cycleId: string, params: { groupId?: string; ownerId?: string } = {}): Promise<GoalTree> {
    return this.ctx.call({ httpMethod: 'GET', path: '/goals/cycles/{cycleId}/objectives' }, { cycleId, query: params })
  }

  async createObjective(body: ObjectiveInput & { cycleId: string; level: string; title: string; keyResults: KeyResultInput[] }): Promise<Objective> {
    return this.ctx.call({ httpMethod: 'POST', path: '/goals/objectives', hasBody: true }, { body })
  }

  async getObjective(objectiveId: string): Promise<Objective> {
    return this.ctx.call({ httpMethod: 'GET', path: '/goals/objectives/{objectiveId}' }, { objectiveId })
  }

  async updateObjective(objectiveId: string, body: ObjectiveInput): Promise<Objective> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/goals/objectives/{objectiveId}', hasBody: true }, { objectiveId, body })
  }

  async deleteObjective(objectiveId: string): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/goals/objectives/{objectiveId}' }, { objectiveId })
  }

  async addKeyResult(objectiveId: string, body: KeyResultInput): Promise<Objective> {
    return this.ctx.call({ httpMethod: 'POST', path: '/goals/objectives/{objectiveId}/key-results', hasBody: true }, { objectiveId, body })
  }

  async updateKeyResult(keyResultId: string, body: Partial<KeyResultInput>): Promise<Objective> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/goals/key-results/{keyResultId}', hasBody: true }, { keyResultId, body })
  }

  async deleteKeyResult(keyResultId: string): Promise<Objective> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/goals/key-results/{keyResultId}' }, { keyResultId })
  }

  async listCheckIns(keyResultId: string): Promise<KeyResultCheckIn[]> {
    return asList<KeyResultCheckIn>(await this.ctx.call({ httpMethod: 'GET', path: '/goals/key-results/{keyResultId}/check-ins' }, { keyResultId }))
  }

  /** Append only: there is no edit route. */
  async checkIn(keyResultId: string, body: { value?: number | null; done?: boolean; health: GoalHealth; note?: string | null }): Promise<Objective> {
    return this.ctx.call({ httpMethod: 'POST', path: '/goals/key-results/{keyResultId}/check-ins', hasBody: true }, { keyResultId, body })
  }

  async myKeyResults(params: { spaceId?: string; pendingCheckIn?: boolean } = {}): Promise<KeyResult[]> {
    return asList<KeyResult>(await this.ctx.call({ httpMethod: 'GET', path: '/goals/my-key-results' }, { query: params }))
  }
}
